import { SchoolPropertyType } from '@schooly/constants';
import { removeObjectUndefinedOrNullValues } from '@schooly/utils/remove-object-undefined-or-null-values';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CancelToken } from 'axios';
import moment from 'moment';
import { useState } from 'react';

import { DEFAULT_DATE_FORMAT } from '../constants';
import { MinimalComment } from './apiTypes/base';
import { ConductArrangeBy } from './apiTypes/conduct';
import {
  ConductEntry,
  ConductEntryPatch,
  ConductEntryPatchRequest,
  ConductStat,
  ConductType,
  ConductTypeCreate,
  ConductTypeHandle,
  ConductTypeUpdate,
  ConductVisibility,
  GetConductEntriesAggregatedResponse,
} from './apiTypes/endpoints/conduct';
import { IColumnSort } from './apiTypes/endpoints/people';
import { ApiError, PagedResponse, SORT_DIRECTION } from './apiTypes/misc';
import { getConductEntriesAggregatePath } from './apiTypes/paths/conduct';
import {
  RQUseInfiniteQueryOptions,
  RQUseMutationOptions,
  RQUseMutationResult,
  RQUseQueryOptions,
} from './apiTypes/query';
import { ConductSelectTypesFilterValue, FilterKeys, UserFilter } from './apiTypes/users';
import * as api from './requests';
import { getSortParams } from './utils/getSortParam';

const DEFAULT_PAGE_SIZE = 50;

const CONDUCT_URL = '/conduct';

export interface ConductEntriesRequest {
  schoolId?: string;
  query?: string;
  filters?: Omit<Partial<UserFilter>, 'conduct_select_types'> & {
    conduct_select_types?: ConductSelectTypesFilterValue[];
  };
  sort?: IColumnSort<keyof ConductEntry>[];
  pageSize?: number;
  pageNumber?: number;
  token?: CancelToken;
}

export interface GroupConductEntriesRequest extends Omit<ConductEntriesRequest, 'schoolId'> {
  groupId?: string;
}

export interface ConductEntriesAggregatedRequest
  extends Omit<ConductEntriesRequest, 'pageSize' | 'pageNumber'> {
  arrangeBy: ConductArrangeBy;
}

export type ConductEntriesCreateRequest = Omit<ConductEntriesRequest, 'filters' | 'sort'> & {
  filters: GetConductEntriesQueryFilters;
  sort?: GetConductEntriesQuerySort;
};

export type GetConductEntriesParams = Omit<ConductEntriesRequest, 'filters' | 'sort'> & {
  filters: GetConductEntriesQueryFilters;
  sort?: GetConductEntriesQuerySort;
};

export interface ConductEntryCreateRequest {
  date: string;
  students: ConductEntryPatch[];
}

export const getConductDefaultSortField = (): IColumnSort<keyof ConductEntry> => ({
  columnTextId: 'student',
  direction: SORT_DIRECTION.ASC,
});

export const getConductDefaultSort = (firstSort?: keyof ConductEntry) => {
  const sort: IColumnSort<keyof ConductEntry>[] = [
    getConductDefaultSortField(), // student always first
    { columnTextId: 'date', direction: SORT_DIRECTION.DESC },
    { columnTextId: 'conduct_type', direction: SORT_DIRECTION.ASC },
  ];

  if (firstSort && firstSort !== 'student') {
    const index = sort.findIndex((item) => item.columnTextId === firstSort);

    if (index > 0) {
      const item = sort.splice(index, 1);
      sort.splice(1, 0, ...item);
    }
  }

  return sort;
};

export const getConductEntries = ({
  schoolId,
  query,
  filters,
  sort = getConductDefaultSort(),
  pageSize = DEFAULT_PAGE_SIZE,
  pageNumber = 1,
  token,
}: ConductEntriesRequest): Promise<PagedResponse<ConductEntry>> => {
  const params = getConductQueryFilters({ schoolId, query, filters, sort });

  return api.get(`${CONDUCT_URL}/entries/for-school/${schoolId}`, {
    params: {
      ...params,
      page_size: pageSize,
      page_number: pageNumber,
    },
    cancelToken: token,
  });
};

export const GET_CONDUCT_ENTRIES_QUERY = `${CONDUCT_URL}/GET_CONDUCT_ENTRIES_QUERY`;

export const CONDUCT_ENTRIES_QUERY_FILTER_KEYS = [
  FilterKeys.Date,
  FilterKeys.ConductStatus,
  FilterKeys.ConductType,
  FilterKeys.ConductSelectTypes,
  FilterKeys.Student,
  FilterKeys.AgeGroup,
  FilterKeys.House,
  FilterKeys.Group,
] as const;

export const CONDUCT_ENTRIES_GROUP_BY_FILTER_KEYS = [FilterKeys.Student] as const;

export type GetConductEntriesQueryFilters = {
  [FilterKeys.Date]?: string[];
  [FilterKeys.ConductStatus]?: ConductVisibility[];
  [FilterKeys.ConductType]?: string[];
  [FilterKeys.ConductSelectTypes]?: ConductSelectTypesFilterValue[];
  [FilterKeys.Student]?: string[];
  [FilterKeys.AgeGroup]?: string[];
  [FilterKeys.House]?: string[];
  [FilterKeys.Group]?: string[];
  [FilterKeys.ConductEntryIds]?: string[];
};

export type GetConductEntriesQuerySort = {
  columnTextId: 'student' | 'date' | 'conduct_type';
  direction: SORT_DIRECTION;
};

const prepareGetConductEntriesQuerySort = (v?: GetConductEntriesQuerySort) => {
  const byConductTypeSort: IColumnSort<keyof ConductEntry> = {
    columnTextId: 'conduct_type',
    direction: SORT_DIRECTION.ASC,
  };

  const byDateSort: IColumnSort<keyof ConductEntry> = {
    columnTextId: 'date',
    direction: SORT_DIRECTION.DESC,
  };

  let defaultQuerySort: IColumnSort<keyof ConductEntry>[] = [
    { columnTextId: 'student', direction: SORT_DIRECTION.ASC },
  ];

  // if we have a sort by `conduct_type` or `date`, it should be first in `sort_by`
  switch (v?.columnTextId) {
    case 'conduct_type':
      defaultQuerySort = [byConductTypeSort, ...defaultQuerySort];
      break;
    case 'date':
      defaultQuerySort = [byDateSort, ...defaultQuerySort];
      break;
    default:
      defaultQuerySort = [...defaultQuerySort, byDateSort, byConductTypeSort];
  }

  return defaultQuerySort.map((sort) => (v && sort.columnTextId === v.columnTextId ? v : sort));
};

const getConductQueryFilters = ({
  query,
  filters,
  sort = getConductDefaultSort(),
}: ConductEntriesRequest) => {
  const sortBy = getSortParams(
    sort.reduce<IColumnSort[]>((prev, item) => {
      switch (item.columnTextId) {
        case 'student':
          prev.push({ columnTextId: 'last_name', direction: item.direction });
          prev.push({ columnTextId: 'given_name', direction: item.direction });
          break;
        default:
          prev.push(item);
      }

      return prev;
    }, []),
  );
  const dateFrom = filters?.date?.[0] || moment().format(DEFAULT_DATE_FORMAT);
  const dateTo = filters?.date?.[1];
  const singleDate =
    filters?.date?.length === 1
      ? filters?.date?.[0] || moment().format(DEFAULT_DATE_FORMAT)
      : undefined;
  const conductSelectTypes = filters?.conduct_select_types?.some(
    (option) => option.conduct_type_options.length,
  )
    ? JSON.stringify(
        filters.conduct_select_types
          .filter((option) => option.conduct_type_options.length)
          .map((option) => ({
            conduct_type_id: option.conduct_type_id,
            conduct_type_options: option.conduct_type_options,
          })),
      )
    : undefined;

  const params = removeObjectUndefinedOrNullValues({
    single_date: singleDate,
    date_from: dateTo ? dateFrom : undefined,
    date_to: dateTo,
    conduct_type_ids: filters?.conduct_type?.length ? filters.conduct_type.join(',') : undefined,
    conduct_select_types: conductSelectTypes,
    student_ids: filters?.student?.length ? filters.student.join(',') : undefined,
    group_ids: filters?.group?.length ? filters.group.join(',') : undefined,
    age_group: filters?.age_group?.length ? filters.age_group.join(',') : undefined,
    house: filters?.house?.length ? filters.house.join(',') : undefined,
    conduct_entry_ids: filters?.conduct_entry_ids?.length
      ? filters.conduct_entry_ids.join(',')
      : undefined,
    visibility: filters?.visibility?.length === 2 ? undefined : filters?.visibility?.[0],
  });

  return {
    ...params,
    search_query: query || undefined,
    sort_by: sortBy,
  };
};

export const useGetConductEntriesQuery = (
  initialParams: GetConductEntriesParams,
  options?: RQUseInfiniteQueryOptions<PagedResponse<ConductEntry>>,
) => {
  const [params, setParams] = useState(initialParams);
  const query = useInfiniteQuery<PagedResponse<ConductEntry>, ApiError>(
    [GET_CONDUCT_ENTRIES_QUERY, params],
    ({ pageParam }) =>
      getConductEntries({
        pageNumber: pageParam,
        ...params,
        sort: prepareGetConductEntriesQuerySort(params.sort),
      }),
    {
      getNextPageParam: (lastPage) => {
        return !lastPage?.total_pages || lastPage?.current_page === lastPage?.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        return firstPage?.current_page ? firstPage?.previous_page : undefined;
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export const GET_CONDUCT_ENTRIES_AGGREGATED_QUERY = `${CONDUCT_URL}/GET_CONDUCT_ENTRIES_AGGREGATED_QUERY`;

export const getConductEntriesAggregated = ({
  schoolId,
  query,
  filters,
  sort,
  arrangeBy,
  token,
}: ConductEntriesAggregatedRequest): Promise<GetConductEntriesAggregatedResponse> => {
  const params = getConductQueryFilters({ schoolId, query, filters, sort });

  return api.get(getConductEntriesAggregatePath({ school_id: schoolId ?? '' }), {
    params: {
      ...params,
      arrange_by: arrangeBy,
    },
    cancelToken: token,
  });
};

export const useGetConductEntriesAggregatedQuery = (
  params: ConductEntriesAggregatedRequest,
  options?: RQUseQueryOptions<GetConductEntriesAggregatedResponse>,
) => {
  return useQuery<GetConductEntriesAggregatedResponse, ApiError>(
    [GET_CONDUCT_ENTRIES_AGGREGATED_QUERY, params],
    () => getConductEntriesAggregated(params),
    options,
  );
};

export const getConductTypesForSchool = (schoolId: string): Promise<ConductType[]> => {
  return api.get(`${CONDUCT_URL}/types/for-school/${schoolId}`);
};

export const GET_CONDUCT_TYPES_FOR_SCHOOL_QUERY = `${CONDUCT_URL}/GET_CONDUCT_TYPES_FOR_SCHOOL_QUERY`;

export const useGetConductTypesForSchoolQuery = (
  schoolId: string,
  options?: RQUseQueryOptions<ConductType[]>,
) => {
  const queryClient = useQueryClient();
  return useQuery<ConductType[], ApiError>(
    [GET_CONDUCT_TYPES_FOR_SCHOOL_QUERY, schoolId],
    () => getConductTypesForSchool(schoolId),
    {
      ...options,
      onSuccess: (data) => {
        for (const conductType of data) {
          queryClient.setQueryData([GET_CONDUCT_TYPE_QUERY, conductType.id], conductType);
        }
      },
    },
  );
};

export const getConductType = (typeId: string): Promise<ConductType> => {
  return api.get(`${CONDUCT_URL}/type/${typeId}`);
};

export const GET_CONDUCT_TYPE_QUERY = `${CONDUCT_URL}/GET_CONDUCT_TYPE_QUERY`;

export const useGetConductTypeQuery = (id: string, options?: RQUseQueryOptions<ConductType>) => {
  return useQuery<ConductType, ApiError>([GET_CONDUCT_TYPE_QUERY, id], () => getConductType(id), {
    ...options,
  });
};

type CreateConductTypesParams = {
  schoolId: string;
  conductTypes: ConductTypeCreate[];
};

export function createConductTypes({
  schoolId,
  conductTypes,
}: CreateConductTypesParams): Promise<ConductType> {
  return api.post(`${CONDUCT_URL}/types/for-school/${schoolId}`, conductTypes);
}

export const useCreateConductTypesMutation = (
  options?: RQUseMutationOptions<ConductType, CreateConductTypesParams>,
): RQUseMutationResult<ConductType, CreateConductTypesParams> => {
  return useMutation(createConductTypes, options);
};

type UpdateConductTypeParams = {
  typeId: string;
  conductType: Partial<ConductTypeUpdate>;
};

export function updateConductType({
  typeId,
  conductType,
}: UpdateConductTypeParams): Promise<ConductType> {
  return api.patch(`${CONDUCT_URL}/type/${typeId}`, conductType);
}

export const useUpdateConductTypeMutation = (
  options?: RQUseMutationOptions<ConductType, UpdateConductTypeParams>,
): RQUseMutationResult<ConductType, UpdateConductTypeParams> => {
  return useMutation(updateConductType, options);
};

export function handleConductTypes({
  schoolId,
  conductTypes,
}: {
  schoolId: string;
  conductTypes: ConductTypeHandle[];
}): Promise<ConductType> {
  return api.post(`${CONDUCT_URL}/handle-conduct-types/${schoolId}`, {
    conduct_types: conductTypes,
  });
}

export function deleteConductType(typeId: string): Promise<any> {
  return api.remove(`${CONDUCT_URL}/type/${typeId}`);
}

export const useDeleteConductTypeMutation = (
  options?: RQUseMutationOptions<ConductType, string>,
): RQUseMutationResult<ConductType, string> => {
  return useMutation(deleteConductType, options);
};

type CreateConductEntryForRelationParams = {
  conductTypeId: string;
  data: ConductEntryCreateRequest;
};

const createConductEntryForRelation = ({
  conductTypeId,
  data,
}: CreateConductEntryForRelationParams) => {
  return api.post(`${CONDUCT_URL}/entries/for-type/${conductTypeId}`, data);
};

export const useCreateConductEntryMutation = (
  options?: RQUseMutationOptions<{ success: string }, CreateConductEntryForRelationParams>,
): RQUseMutationResult<{ success: string }, CreateConductEntryForRelationParams> => {
  return useMutation(createConductEntryForRelation, options);
};

type UpdateConductEntryParams = {
  id: string;
  data: ConductEntryPatchRequest;
};

const updateConductEntry = ({ id, data }: UpdateConductEntryParams) => {
  return api.patch(`${CONDUCT_URL}/entries/${id}`, data);
};

export const useUpdateConductEntryMutation = (
  options?: RQUseMutationOptions<{ success: string }, UpdateConductEntryParams>,
): RQUseMutationResult<{ success: string }, UpdateConductEntryParams> => {
  return useMutation(updateConductEntry, options);
};

export const deleteConductEntry = (conductEntryId: string) => {
  return api.remove(`${CONDUCT_URL}/entries/${conductEntryId}`);
};

export const useDeleteConductEntryMutation = (
  options?: RQUseMutationOptions<{ success: string }, string>,
): RQUseMutationResult<{ success: string }, string> => {
  return useMutation(deleteConductEntry, options);
};

type AddEntryCommentResponse = { success: string; comment_id: string };

export const addEntryComment = (
  entryId: string,
  comment: MinimalComment,
): Promise<AddEntryCommentResponse> => {
  return api.post(`${CONDUCT_URL}/comment/for-entry/${entryId}`, comment);
};

type AddEntryCommentMutationParams = {
  entryId: string;
  comment: MinimalComment;
};

export const useAddEntryCommentMutation = (
  options?: RQUseMutationOptions<AddEntryCommentResponse, AddEntryCommentMutationParams>,
): RQUseMutationResult<AddEntryCommentResponse, AddEntryCommentMutationParams> => {
  return useMutation(
    (params: AddEntryCommentMutationParams) => addEntryComment(params.entryId, params.comment),
    options,
  );
};

export const editEntryComment = (
  entryId: string,
  commentId: string,
  comment: Partial<MinimalComment>,
): Promise<AddEntryCommentResponse> => {
  return api.patch(`${CONDUCT_URL}/comment/${commentId}`, {
    conduct_entry_id: entryId,
    ...comment,
  });
};

type EditEntryCommentMutationParams = {
  entryId: string;
  commentId: string;
  comment: Partial<MinimalComment>;
};

export const useEditEntryCommentMutation = (
  options?: RQUseMutationOptions<AddEntryCommentResponse, EditEntryCommentMutationParams>,
): RQUseMutationResult<AddEntryCommentResponse, EditEntryCommentMutationParams> => {
  return useMutation(
    (params: EditEntryCommentMutationParams) =>
      editEntryComment(params.entryId, params.commentId, params.comment),
    options,
  );
};

export type ExportConductForRelationParams = {
  relationId: string;
  [FilterKeys.Date]?: string[];
};

export const exportConductForRelation = (
  reqParams: ExportConductForRelationParams,
): Promise<ArrayBuffer> => {
  return api.get(`/export${CONDUCT_URL}/for-relation/${reqParams.relationId}`, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/csv',
    },
    params: reqParams[FilterKeys.Date]?.length
      ? {
          date_from: reqParams[FilterKeys.Date][0],
          date_to: reqParams[FilterKeys.Date][1],
        }
      : undefined,
  });
};

export const useExportConductForRelation = (
  options?: RQUseMutationOptions<ArrayBuffer, ExportConductForRelationParams>,
): RQUseMutationResult<ArrayBuffer, ExportConductForRelationParams> => {
  return useMutation(exportConductForRelation, options);
};

export type ExportConductForGroupParams = {
  group_id: string;
  [FilterKeys.Date]?: string[];
  [FilterKeys.ConductType]?: string[];
  [FilterKeys.ConductStatus]?: ConductVisibility[];
};
export const exportConductForGroup = ({
  query,
  filters,
  groupId,
}: GroupConductEntriesRequest): Promise<ArrayBuffer> => {
  const params = getConductQueryFilters({ query, filters });

  return api.get(`/export${CONDUCT_URL}/for-group/${groupId}`, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/csv',
    },
    params: {
      date_from: params.date_from,
      date_to: params.date_to,
      visibility: params.visibility,
      conduct_type_ids: params.conduct_type_ids,
      search_query: params.search_query,
    },
  });
};

export const useExportConductForGroup = (
  options?: RQUseMutationOptions<ArrayBuffer, GroupConductEntriesRequest>,
): RQUseMutationResult<ArrayBuffer, GroupConductEntriesRequest> => {
  return useMutation(
    (params) =>
      exportConductForGroup({
        ...params,
      }),
    options,
  );
};

export const useExportConductForPage = (
  options?: RQUseMutationOptions<ArrayBuffer, ConductEntriesCreateRequest>,
): RQUseMutationResult<ArrayBuffer, ConductEntriesCreateRequest> => {
  return useMutation(
    (params) =>
      exportConductForPage({
        ...params,
        sort: prepareGetConductEntriesQuerySort(params.sort),
      }),
    options,
  );
};

export const exportConductForPage = ({
  schoolId,
  query,
  filters,
  sort,
}: ConductEntriesRequest): Promise<ArrayBuffer> => {
  const params = getConductQueryFilters({ schoolId, query, filters, sort });

  return api.get(`/export${CONDUCT_URL}/${schoolId}`, {
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/csv',
    },
    params,
  });
};

export interface ConductStatsRequest {
  schoolId: string;
  conduct_type_id: string;
  school_property_type: SchoolPropertyType;
  date_from?: string;
  date_to?: string;
  group_ids?: string;
  student_ids?: string;
  age_group_ids?: string;
  house_ids?: string;
  visibility?: ConductVisibility;
}

export const getConductStats = ({
  params: { schoolId, ...params },
  token,
}: {
  params: ConductStatsRequest;
  token?: CancelToken;
}): Promise<ConductStat[]> => {
  return api.get(`${CONDUCT_URL}/stats/for-school/${schoolId}`, {
    params,
    cancelToken: token,
  });
};

export const GET_CONDUCT_STATS_QUERY_FILTER_KEYS = [
  FilterKeys.Date,
  FilterKeys.AgeGroup,
  FilterKeys.Student,
  FilterKeys.House,
  FilterKeys.Group,
  FilterKeys.ConductStatus,
] as const;

export type GetConductStatsQueryFilters = {
  [FilterKeys.Date]?: string[];
  [FilterKeys.AgeGroup]?: string[];
  [FilterKeys.Student]?: string[];
  [FilterKeys.House]?: string[];
  [FilterKeys.Group]?: string[];
  [FilterKeys.ConductStatus]?: ConductVisibility[];
};

type GetConductStatsQueryParams = {
  schoolId: string;
  conduct_type_id: string;
  school_property_type: SchoolPropertyType;
  filters?: GetConductStatsQueryFilters;
};

export const GET_CONDUCT_STATS_QUERY = `${CONDUCT_URL}/GET_CONDUCT_STATS_QUERY`;

export const useGetConductStatsQuery = (
  params: GetConductStatsQueryParams,
  options?: RQUseQueryOptions<ConductStat[]>,
) => {
  return useQuery<ConductStat[], ApiError>(
    [GET_CONDUCT_STATS_QUERY, params],
    () => {
      const { schoolId, conduct_type_id, school_property_type, filters } = params;

      const requestParams: ConductStatsRequest = {
        schoolId,
        conduct_type_id,
        school_property_type,
        date_from: filters?.date?.[0] ? filters.date[0] : undefined,
        date_to: filters?.date?.[1] ? filters.date[1] : undefined,
        group_ids: filters?.group?.length ? filters.group.join(',') : undefined,
        student_ids: filters?.student?.length ? filters.student.join(',') : undefined,
        age_group_ids: filters?.age_group?.length ? filters.age_group.join(',') : undefined,
        house_ids: filters?.house?.length ? filters.house.join(',') : undefined,
        visibility: filters?.visibility?.length
          ? filters.visibility.includes(ConductVisibility.PUBLISHED)
            ? ConductVisibility.PUBLISHED
            : ConductVisibility.NOT_PUBLISHED
          : undefined,
      };

      return getConductStats({ params: requestParams });
    },
    {
      ...options,
    },
  );
};
